import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, ExternalLink, FileText, History, MailCheck, RefreshCw, Send, Trash2, UserRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, API, getToken, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { SUBMITTAL_STATUS_META, DECISIONS, fmtBytes, fmtStamp } from "@/components/submittals/submittalMeta";

const DECISION_TONE = {
  approved: "data-[state=on]:border-emerald-500 data-[state=on]:bg-emerald-500/10 data-[state=on]:text-emerald-300",
  approved_with_comments: "data-[state=on]:border-emerald-500 data-[state=on]:bg-emerald-500/10 data-[state=on]:text-emerald-300",
  revise_resubmit: "data-[state=on]:border-amber-500 data-[state=on]:bg-amber-500/10 data-[state=on]:text-amber-300",
  rejected: "data-[state=on]:border-destructive data-[state=on]:bg-destructive/10 data-[state=on]:text-destructive",
};

export const fileUrl = (subId, fileId) => `${API}/submittals/${subId}/files/${fileId}?token=${encodeURIComponent(getToken() || "")}`;

/** One revision block — files + note + the review it received. Shared by the builder dialog and the contractor card. */
export const RevisionBlock = ({ sub, rev, isCurrent, testPrefix = "submittal-rev" }) => {
  const review = rev.review;
  const rm = review ? SUBMITTAL_STATUS_META[review.decision] : null;
  return (
    <div className={`rounded-lg border p-3 ${isCurrent ? "border-primary/40 bg-primary/5" : "border-border bg-muted/20"}`} data-testid={`${testPrefix}-${rev.rev}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
        <p className="text-sm font-semibold">
          Revision {rev.rev}
          {isCurrent && <span className="ml-2 rounded bg-primary/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-primary">Current</span>}
        </p>
        <p className="text-[11px] text-muted-foreground">
          {fmtStamp(rev.at)} · {rev.by_kind === "builder" ? `logged by ${rev.logged_by || "the builder"} for ${rev.by_name}` : `lodged by ${rev.by_name || "the submitter"}`}
        </p>
      </div>
      {rev.note && <p className="mt-1.5 whitespace-pre-wrap text-sm">{rev.note}</p>}
      {(rev.files || []).length > 0 ? (
        <ul className="mt-2 space-y-1">
          {rev.files.map((f) => (
            <li key={f.id}>
              <a
                href={fileUrl(sub.id, f.id)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex max-w-full items-center gap-1.5 rounded-md border border-border bg-background px-2 py-1 text-xs hover:border-primary/50 hover:text-primary"
                data-testid={`${testPrefix}-${rev.rev}-file-${f.id}`}
              >
                <FileText className="h-3.5 w-3.5 shrink-0 text-primary" />
                <span className="truncate">{f.filename}</span>
                <span className="text-muted-foreground">{fmtBytes(f.size)}</span>
                <ExternalLink className="h-3 w-3 shrink-0 text-muted-foreground" />
              </a>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-1.5 text-xs italic text-muted-foreground">No files on this revision</p>
      )}
      {review ? (
        <div className="mt-3 rounded-md border border-border bg-background p-2.5" data-testid={`${testPrefix}-${rev.rev}-review`}>
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline" className={rm?.cls || ""}>{rm?.label || review.decision}</Badge>
            <span className="text-[11px] text-muted-foreground">by {review.by_name || "the builder"} · {fmtStamp(review.at)}</span>
          </div>
          {review.comments && <p className="mt-1.5 whitespace-pre-wrap text-sm">{review.comments}</p>}
        </div>
      ) : isCurrent ? (
        <p className="mt-2 text-xs text-sky-300">Awaiting the builder's review</p>
      ) : null}
    </div>
  );
};

/**
 * Phase 136 — builder's view of a submittal: who / what / when, the revision history with files, and the review form
 * (approved · approved with comments · revise & resubmit · rejected → e-mail to the submitter).
 */
export const SubmittalDetailDialog = ({ submittal: initial, onOpenChange, onChanged, onResubmit }) => {
  const [sub, setSub] = useState(initial);
  const [decision, setDecision] = useState("");
  const [comments, setComments] = useState("");
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setSub(initial);
    setDecision("");
    setComments("");
    setNotify(true);
  }, [initial]);

  useEffect(() => {
    if (!initial?.id) return;
    api.get(`/submittals/${initial.id}`).then((r) => setSub(r.data)).catch(() => {});
  }, [initial?.id]);

  if (!sub) return null;
  const sm = SUBMITTAL_STATUS_META[sub.status] || SUBMITTAL_STATUS_META.pending_review;
  const by = sub.submitted_by || {};
  const revs = sub.revisions || [];
  const current = revs[revs.length - 1];
  const canReview = !!current && !current.review;

  const review = async () => {
    if (!decision) return toast.error("Pick a decision");
    if (["revise_resubmit", "rejected"].includes(decision) && !comments.trim()) return toast.error("Tell the submitter what needs to change");
    setBusy(true);
    try {
      const r = await api.post(`/submittals/${sub.id}/review`, { decision, comments: comments.trim(), notify });
      setSub(r.data);
      const n = r.data.notice || {};
      if (n.emailed) toast.success(`${sub.number} marked ${SUBMITTAL_STATUS_META[decision]?.label} — ${by.name || "the submitter"} emailed`);
      else toast.success(`${sub.number} marked ${SUBMITTAL_STATUS_META[decision]?.label}`, { description: n.error ? `Outcome e-mail not sent: ${n.error}` : undefined });
      setDecision("");
      setComments("");
      onChanged?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete ${sub.number} and all its revisions? This can't be undone.`)) return;
    try {
      await api.delete(`/submittals/${sub.id}`);
      toast.success(`${sub.number} deleted`);
      onChanged?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <Dialog open={!!sub} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" data-testid="submittal-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2 pr-6">
            <span className="rounded bg-primary/10 px-2 py-0.5 text-sm font-bold text-primary" data-testid="submittal-detail-number">{sub.number}</span>
            <span className="min-w-0 flex-1 truncate" data-testid="submittal-detail-title">{sub.title}</span>
            <Badge variant="outline" className={sm.cls} data-testid="submittal-detail-status-badge">{sm.label}</Badge>
            {sub.overdue && <Badge variant="outline" className={SUBMITTAL_STATUS_META.rejected.cls} data-testid="submittal-detail-overdue-badge">Review overdue</Badge>}
          </DialogTitle>
          <DialogDescription data-testid="submittal-detail-meta">
            {sub.job_name} · {sub.type_label}{sub.spec_ref ? ` · ${sub.spec_ref}` : ""} · revision {sub.current_revision} of {sub.revision_count}
            {sub.required_by ? ` · review needed by ${fmtDate(sub.required_by)}` : ""}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-muted/20 p-3" data-testid="submittal-detail-submitter">
            <UserRound className="h-5 w-5 text-primary" />
            <div className="min-w-0 flex-1 text-sm">
              <p className="font-semibold">{by.name || "Unknown submitter"}{by.company ? <span className="font-normal text-muted-foreground"> · {by.company}</span> : null}</p>
              <p className="text-xs text-muted-foreground">
                {by.kind === "supplier" ? "Supplier" : "Contractor"}{by.trade ? ` · ${by.trade}` : ""}{by.email ? ` · ${by.email}` : " · no email on file"}
                {sub.on_behalf ? ` · logged by ${sub.lodged_by_name} on their behalf` : " · lodged from the contractor portal"} · {fmtDate(sub.created_at)}
              </p>
            </div>
            {sub.submitter_email_status === "sent" && current?.review && (
              <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-semibold text-emerald-300" title={`Outcome emailed ${fmtStamp(sub.submitter_emailed_at)}`}>
                <MailCheck className="h-3 w-3" /> Outcome emailed
              </span>
            )}
          </div>

          {sub.description && (
            <div className="rounded-lg border border-border bg-muted/20 p-3" data-testid="submittal-detail-description">
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-muted-foreground">Description</p>
              <p className="mt-1 whitespace-pre-wrap text-sm">{sub.description}</p>
            </div>
          )}

          <div className="space-y-2" data-testid="submittal-detail-revisions">
            <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.14em] text-muted-foreground"><History className="h-3.5 w-3.5" /> Revision history</p>
            {[...revs].reverse().map((rev) => (
              <RevisionBlock key={rev.rev} sub={sub} rev={rev} isCurrent={rev.rev === current?.rev} />
            ))}
          </div>

          <Separator />

          {canReview ? (
            <div className="space-y-3 rounded-lg border border-border bg-background p-3" data-testid="submittal-review-form">
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-muted-foreground">Review revision {current.rev}</p>
              <ToggleGroup type="single" value={decision} onValueChange={(v) => v && setDecision(v)} className="grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="submittal-review-decision">
                {DECISIONS.map((d) => (
                  <ToggleGroupItem key={d.key} value={d.key} className={`h-auto flex-col items-start gap-0.5 border border-border px-3 py-2 text-left ${DECISION_TONE[d.key]}`} data-testid={`submittal-review-decision-${d.key}`}>
                    <span className="text-sm font-semibold">{d.label}</span>
                    <span className="text-[11px] font-normal text-muted-foreground">{d.hint}</span>
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.14em] text-muted-foreground">
                  Comments {["revise_resubmit", "rejected"].includes(decision) ? <span className="text-amber-300">(required)</span> : "(optional)"}
                </Label>
                <Textarea value={comments} onChange={(e) => setComments(e.target.value)} rows={3} placeholder="What needs to change, or any conditions on the approval…" data-testid="submittal-review-comments" />
              </div>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Switch id="submittal-review-notify" checked={notify} onCheckedChange={setNotify} data-testid="submittal-review-notify" />
                  <Label htmlFor="submittal-review-notify" className="text-sm">E-mail the outcome to {by.name || "the submitter"}{by.email ? "" : " (no email on file)"}</Label>
                </div>
                <Button onClick={review} disabled={busy || !decision} data-testid="submittal-review-submit-btn">
                  <CheckCircle2 className="mr-1.5 h-4 w-4" /> {busy ? "Saving…" : "Record decision"}
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground" data-testid="submittal-reviewed-note">
              Revision {current?.rev} has been reviewed. {sub.status === "revise_resubmit" ? "Waiting on the contractor to lodge a revised submission — or log it on their behalf." : ""}
            </p>
          )}

          <div className="flex flex-wrap justify-between gap-2 border-t border-border pt-4">
            <Button variant="ghost" className="text-muted-foreground hover:text-destructive" onClick={remove} data-testid="submittal-detail-delete-btn">
              <Trash2 className="mr-1.5 h-4 w-4" /> Delete
            </Button>
            <div className="flex gap-2">
              {sub.status !== "rejected" && onResubmit && (
                <Button variant="outline" onClick={() => onResubmit(sub)} data-testid="submittal-detail-resubmit-btn">
                  <RefreshCw className="mr-1.5 h-4 w-4" /> Log revision on their behalf
                </Button>
              )}
              <Button variant="secondary" onClick={() => onOpenChange(false)} data-testid="submittal-detail-close-btn">
                <Send className="mr-1.5 h-4 w-4 rotate-180" /> Done
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
